import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiLock as Lock, FiEye as Eye, FiEyeOff as EyeOff } from 'react-icons/fi';
import supabase from '../supabase/supabase';
import { useAuth } from '../context/AuthContext';

const ResetPasswordPage = () => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'expired'
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // The Supabase SDK processes the hash automatically on page load.
    // Listen for PASSWORD_RECOVERY event, and also check for an existing
    // session in case the SDK processed the hash before this component mounted.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setStatus('ready');
      }
    });

    // Check if session already exists (hash processed before mount)
    supabase.auth.getSession().then(({ data: { session } }) => {
      const hash = window.location.hash;
      if (session && hash.includes('type=recovery')) {
        setStatus('ready');
      } else if (!hash.includes('access_token')) {
        // No recovery params in URL at all — invalid direct navigation
        setStatus('expired');
      }
      // Otherwise keep 'loading' until PASSWORD_RECOVERY event fires
    });

    return () => subscription.unsubscribe();
  }, []);

  const validate = () => {
    const errs = {};
    if (!password) errs.password = 'New password is required.';
    else if (password.length < 8) errs.password = 'Password must be at least 8 characters.';
    if (!confirmPassword) errs.confirmPassword = 'Please confirm your password.';
    else if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setIsLoading(true);
    const result = await updatePassword(password);
    setIsLoading(false);

    if (result.success) {
      await supabase.auth.signOut();
      navigate('/login', {
        state: { message: 'Password updated successfully. Please sign in with your new password.' },
      });
    } else {
      if (result.error.toLowerCase().includes('expired') || result.error.toLowerCase().includes('invalid')) {
        setStatus('expired');
      } else {
        setSubmitError(result.error);
      }
    }
  };

  const inputCls = (field) =>
    `w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent transition-all duration-200 ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  // ── Loading ──
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // ── Expired / Invalid ──
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <Lock size={28} className="text-red-500" />
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Expired</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              This password reset link has expired or is invalid. Reset links are only valid for a short time.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6 space-y-3">
            <Link
              to="/forgot-password"
              className="block w-full bg-[#DC143C] text-white py-3 rounded-lg font-semibold hover:bg-[#B01030] transition-colors text-center text-sm"
            >
              Request a New Reset Link
            </Link>
            <Link
              to="/login"
              className="block text-sm text-[#0038A8] hover:underline text-center"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Set new password form ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center space-x-3 group mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#DC143C] to-[#B01030] rounded-lg flex items-center justify-center group-hover:shadow-lg transition-all duration-200">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <div className="text-left">
              <div className="text-lg font-bold text-gray-900">Pinoy General Insurance</div>
              <div className="text-xs text-gray-600">Services</div>
            </div>
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Set New Password</h2>
          <p className="text-gray-600">Choose a strong password for your account.</p>
        </div>

        <div className="bg-white py-8 px-8 shadow-xl rounded-xl border border-gray-200">
          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {submitError}
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: '' })); }}
                  className={inputCls('password')}
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setErrors((p) => ({ ...p, confirmPassword: '' })); }}
                  className={inputCls('confirmPassword')}
                  placeholder="Repeat your new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#DC143C] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B01030] focus:ring-4 focus:ring-[#DC143C]/20 transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <><div className="spinner"></div> Updating Password...</>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
